/**
 * Location view (S17, direct PoI visit) — a trimmed sibling of index.ts's
 * `startBaseView`, sharing the same sim/render/camera building blocks but
 * without any of the settlement-only UI (NPC dialog, vendor panel, minimap,
 * paperdoll, world-map launcher). Rule 10: "No ambient NPCs in location
 * maps" — so there is nothing here to talk to, only loot objects to search
 * and an Exit action back to the settlement.
 *
 * Free-roam + loot only (no enemies) — see location-state.ts's header and
 * sprint-2.md's S17 notes for why: Battleground/PvP don't exist yet to
 * resolve a fight, so `LocationScenario.enemySet` is never read anywhere in
 * this feature, front or back.
 */
import * as THREE from 'three';
import { DEFAULT_CAMERA } from './types';
import { buildLocationWorld } from './data/maps/location-templates';
import { createBaseSim } from './sim/base-sim';
import { buildScene } from './render/scene';
import { buildPlayerShadow } from './render/player-shadow';
import { createPlayerAnimator } from './render/character-animations';
import { createNameTags, playerDisplayName } from './render/name-tags';
import { CameraController } from './render/camera-controller';
import { Picker } from './render/picking';
import { createPlayerControls } from './player-controls';
import { keyName, trackHeldKeys } from './key-input';
import { injectStyles } from './ui/styles';
import {
  locationStateStore, exitLocation, searchObject,
} from '../state/location-state';
import type { LocationInstanceView } from '../state/location-state.types';
import { authStore } from '../../core/state/auth-store';

/** "scrap_metal" -> "Scrap Metal" — no item catalog dependency for a first pass (see file header). */
function prettyItemKey(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function startLocationView(
  root: HTMLElement,
  instance: LocationInstanceView,
  onExit: () => void,
  playerModel?: THREE.Object3D,
): () => void {
  const cleanups: Array<() => void> = [];
  const removeStyles = injectStyles();
  let torndown = false;

  const world = buildLocationWorld(instance);
  const searchedLocally = new Set(world.objects.filter((o) => !o.interactable).map((o) => o.lootObjectId).filter(Boolean));

  root.innerHTML = '';
  const container = document.createElement('div');
  container.className = 'bv-root';
  root.appendChild(container);

  const renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: 'high-performance' });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(container.clientWidth || 1, container.clientHeight || 1);
  // The resize handler below passes `updateStyle: false`, so the pixel size
  // this call wrote into the canvas's inline style would otherwise stick: a
  // rotated phone kept a landscape-sized canvas and showed a stretched crop.
  renderer.domElement.style.width = '100%';
  renderer.domElement.style.height = '100%';
  container.appendChild(renderer.domElement);

  const sim = createBaseSim(world);
  const sceneHandles = buildScene(world, playerModel);
  // Same rigged-model animation runtime as the settlement (index.ts) — see
  // character-animation-runtime.md Phase 1. This view now drives it from the
  // same shared control scheme too (see `controls` below); it previously fed
  // hardcoded constants, which made walk/crouch/aim animations unreachable
  // here no matter what the player pressed.
  const playerAnimator = createPlayerAnimator(sceneHandles.player);
  cleanups.push(() => playerAnimator.dispose());
  const cam = new CameraController(aspectOf(container), DEFAULT_CAMERA);
  const picker = new Picker();
  // Same desktop aim/gait/crouch scheme as the settlement, from the shared
  // module (player-controls.ts) rather than a second copy. No
  // `isInputBlocked`: this view has no modal panel that owns the keyboard —
  // its only UI is an Exit button, a prompt and a toast, none of which take
  // focus or swallow keys.
  const controls = createPlayerControls({
    container,
    canvas: renderer.domElement,
    picker,
    getCamera: () => cam.camera,
  });
  cleanups.push(() => controls.dispose());
  const reducedMotionMql = window.matchMedia('(prefers-reduced-motion: reduce)');

  const playerShadow = buildPlayerShadow();
  sceneHandles.scene.add(playerShadow);

  // ---- name tag: the player's own, and only theirs ----
  // A location world holds no `kind: 'npc'` bodies at all (rule 10, and
  // `buildLocationWorld` places only loot), so `createNameTags` finds nothing
  // to label and this is the player's label alone. Built here anyway rather
  // than special-cased: the day a location map gets a hostile body placed in
  // it, its red tag appears with no further wiring.
  const nameTags = createNameTags(world);
  sceneHandles.scene.add(nameTags.object);
  const unsubNameTags = authStore.subscribe(() => {
    nameTags.setPlayerName(playerDisplayName(authStore.get().user));
  });
  cleanups.push(unsubNameTags, () => nameTags.dispose());

  // ---- UI: exit button + search prompt + loot toast ----
  const exitBtn = document.createElement('button');
  exitBtn.type = 'button';
  exitBtn.className = 'bv-loc-exit';
  exitBtn.textContent = 'Exit';
  container.appendChild(exitBtn);

  const prompt = document.createElement('div');
  prompt.className = 'bv-prompt';
  prompt.hidden = true;
  container.appendChild(prompt);

  const toast = document.createElement('div');
  toast.className = 'bv-loc-toast';
  toast.hidden = true;
  container.appendChild(toast);
  let toastTimer = 0;

  const showToast = (message: string): void => {
    window.clearTimeout(toastTimer);
    toast.textContent = message;
    toast.hidden = false;
    toastTimer = window.setTimeout(() => { toast.hidden = true; }, 3500);
  };

  const unsubscribeSearch = locationStateStore.subscribe((s) => {
    if (!s.lastSearch) return;
    const { items, objectId } = s.lastSearch;
    if (searchedLocally.has(objectId)) return; // already toasted this one
    searchedLocally.add(objectId);
    const obj = world.objects.find((o) => o.lootObjectId === objectId);
    if (obj) obj.interactable = false;
    if (items.length === 0) {
      showToast('Nothing but dust.');
    } else {
      showToast(items.map((i) => `+${i.qty} ${prettyItemKey(i.itemKey)}`).join(', '));
    }
  });
  cleanups.push(unsubscribeSearch);

  const doExit = (): void => {
    if (torndown) return;
    void exitLocation();
    onExit();
  };
  exitBtn.addEventListener('click', doExit);
  cleanups.push(() => exitBtn.removeEventListener('click', doExit));

  // ---- input ----
  const keys = new Set<string>();
  let hoverId: string | null = null;
  let dragging = false;
  let dragMoved = 0;
  let lastPointerX = 0;
  let promptObjectId: string | null = null;

  const trySearch = (): void => {
    const snap = sim.getSnapshot();
    const lootId = snap.nearestInteractable?.lootObjectId;
    if (!lootId || searchedLocally.has(lootId)) return;
    void searchObject(lootId);
  };

  const onKeyDown = (e: KeyboardEvent): void => {
    keys.add(keyName(e));
    if (keyName(e) === 'e') trySearch();
    if (e.key === 'Escape') doExit();
    // Crouch ('c') and gait (CapsLock).
    controls.handleKeyDown(e);
  };
  const onKeyUp = (e: KeyboardEvent): void => {
    keys.delete(keyName(e));
    // Gait (CapsLock) — the release transition fires no keydown on macOS.
    // See `syncGait` in player-controls.ts.
    controls.handleKeyUp(e);
  };

  const onPointerDown = (e: PointerEvent): void => {
    // RMB (aim entry) and cursor tracking belong to `controls`, which has its
    // own listener on `container`.
    if (e.button !== 0 || e.target !== renderer.domElement) return;
    // LMB while aiming would be the attack button (§3.7). There is nothing to
    // attack in a location (free-roam + loot only, no enemies — see the file
    // header), so it is swallowed rather than starting a camera-drag, keeping
    // aim and drag mutually exclusive exactly as they are in the settlement.
    if (controls.aiming) return;
    dragging = true;
    dragMoved = 0;
    lastPointerX = e.clientX;
    container.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: PointerEvent): void => {
    hoverId = picker.pick(e.clientX, e.clientY, container, cam.camera, sceneHandles.pickables);
    if (dragging) {
      const dx = e.clientX - lastPointerX;
      lastPointerX = e.clientX;
      dragMoved += Math.abs(dx);
      cam.rotate(dx);
    }
  };
  const onPointerUp = (e: PointerEvent): void => {
    if (dragging && dragMoved < 5) {
      const near = sim.getSnapshot().nearestInteractable;
      if (hoverId && near && near.objectId === hoverId) trySearch();
    }
    dragging = false;
    if (container.hasPointerCapture(e.pointerId)) container.releasePointerCapture(e.pointerId);
  };
  const onWheel = (e: WheelEvent): void => {
    e.preventDefault();
    cam.zoom(Math.sign(e.deltaY));
  };

  container.addEventListener('pointerdown', onPointerDown);
  container.addEventListener('pointermove', onPointerMove);
  container.addEventListener('pointerup', onPointerUp);
  container.addEventListener('wheel', onWheel, { passive: false });
  window.addEventListener('keydown', onKeyDown);
  window.addEventListener('keyup', onKeyUp);
  // Held keys are dropped when their keyup can be lost — see key-input.ts.
  cleanups.push(trackHeldKeys(keys));
  cleanups.push(
    () => container.removeEventListener('pointerdown', onPointerDown),
    () => container.removeEventListener('pointermove', onPointerMove),
    () => container.removeEventListener('pointerup', onPointerUp),
    () => container.removeEventListener('wheel', onWheel),
    () => window.removeEventListener('keydown', onKeyDown),
    () => window.removeEventListener('keyup', onKeyUp),
  );

  const resize = (): void => {
    const w = container.clientWidth || 1;
    const h = container.clientHeight || 1;
    renderer.setSize(w, h, false);
    cam.setAspect(w / h);
  };
  const ro = new ResizeObserver(resize);
  ro.observe(container);
  cleanups.push(() => ro.disconnect());
  resize();

  let raf = 0;
  let last = performance.now();

  const frame = (now: number): void => {
    raf = requestAnimationFrame(frame);
    const dt = Math.min((now - last) / 1000, 0.05);
    last = now;

    controls.updateAim(sim.getSnapshot().player.position);

    const input = {
      moveX: (keys.has('d') || keys.has('arrowright') ? 1 : 0) - (keys.has('a') || keys.has('arrowleft') ? 1 : 0),
      moveZ: (keys.has('w') || keys.has('arrowup') ? 1 : 0) - (keys.has('s') || keys.has('arrowdown') ? 1 : 0),
      aimYaw: controls.simAimYaw,
      gait: controls.gait,
      crouched: controls.crouched,
    };
    sim.update(dt, input, cam.yawValue);

    const snap = sim.getSnapshot();
    sceneHandles.player.position.set(snap.player.position.x, 0, snap.player.position.z);
    sceneHandles.player.rotation.y = snap.player.facing;
    playerShadow.position.set(snap.player.position.x, playerShadow.position.y, snap.player.position.z);
    playerShadow.rotation.y = snap.player.facing;
    cam.update(snap.player.position);
    playerAnimator.update(dt, {
      moving: snap.player.moving,
      gait: controls.gait,
      crouched: controls.crouched,
      aiming: controls.aiming,
      dialogOpen: false, // no NPCs in a location map (rule 10) — nothing to talk to
    });

    const inRange = !!snap.nearestInteractable && snap.nearestInteractable.objectId === hoverId;
    sceneHandles.setHovered(hoverId, inRange);
    sceneHandles.updateAmbience(dt, reducedMotionMql.matches);
    sceneHandles.updateGrass(dt, cam.yawValue);
    // Authored fire/smoke (`WorldData.spriteSpawns`), advanced the way the
    // settlement advances it. Without this call every placed campfire renders
    // as a frozen first frame — lit, in place, and not burning. Caught by
    // `sprite-fx-tick.test.ts`, which discovers a view by the grass it ticks.
    sceneHandles.spriteSpawns.update(dt);
    nameTags.update(snap.player.position, snap.actors);

    renderer.render(sceneHandles.scene, cam.camera);

    const nearLoot = snap.nearestInteractable?.lootObjectId;
    if (nearLoot && !searchedLocally.has(nearLoot)) {
      if (nearLoot !== promptObjectId) {
        promptObjectId = nearLoot;
        prompt.innerHTML = 'Press <b>E</b> to search';
      }
      prompt.hidden = false;
    } else {
      promptObjectId = null;
      prompt.hidden = true;
    }
  };
  raf = requestAnimationFrame(frame);

  return () => {
    torndown = true;
    window.clearTimeout(toastTimer);
    cancelAnimationFrame(raf);
    for (const fn of cleanups) fn();
    exitBtn.remove();
    prompt.remove();
    toast.remove();
    sceneHandles.dispose();
    renderer.dispose();
    renderer.forceContextLoss();
    renderer.domElement.remove();
    removeStyles();
    container.remove();
  };
}

function aspectOf(el: HTMLElement): number {
  const w = el.clientWidth || 1;
  const h = el.clientHeight || 1;
  return w / h;
}
